import React, { useEffect } from 'react';
import type { KeyboardEvent } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
import { differenceInDays } from 'date-fns';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import CrossIcon from '@atlaskit/icon/glyph/cross';

import { useOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { TRIAL_COMPONENTS_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

import { CompliantWarningBanner, BannerLink } from './CompliantWarningBanner';
import { COMPLIANT_BANNER_HEIGHT } from './GracePeriodBanner';

const i18n = defineMessages({
	trialEndedWarning: {
		id: 'trial-components.trial.ended.warning',
		defaultMessage: 'Your trial has ended.',
		description: 'Inform the user that their trial has ended',
	},
	remainingDaysWarning: {
		id: 'trial-components.trial.ended.remaining.days.warning',
		defaultMessage:
			'We‘re giving you an extra {numberOfDaysLeft, plural, one {# day} other {# days}} to keep all {edition} features.',
		description:
			'Let the user know how many days they have left before they get downgraded to Free plan',
	},
	lastDayWarning: {
		id: 'trial-components.trial.ended.last.day.warning',
		defaultMessage: 'Today is the last day to keep all {edition} features.',
		description: 'Remind the user that it is their last day of their trial',
	},
	linkToPaymentDetails: {
		id: 'trial-components.banner.trial.ended.link-to-payment-details',
		defaultMessage: 'Add payment details to stay on {edition}.',
		description: 'CTA link to add payment details',
	},
	dismissIcon: {
		id: 'trial-components.banner.icons.dismiss',
		defaultMessage: 'Dismiss button with cross icon',
		description: 'Accessibility label for an dismiss icon',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	cursor: 'pointer',
});

const capitalize = (string) => {
	const lowerCaseString = string.toLowerCase();
	return lowerCaseString.charAt(0).toUpperCase() + lowerCaseString.slice(1);
};

interface CompliantBannerData {
	linkToAddBilling: string;
	deactivationDate: Date;
	licenseConsumingUserCount: number;
	edition: string;
	hideBanner: () => void;
}

export const CompliantBannerMessage = ({
	linkToAddBilling,
	deactivationDate,
	licenseConsumingUserCount,
	edition,
	hideBanner,
}: CompliantBannerData) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useOnboardingState();
	const { formatMessage } = useIntl();

	const daysLeft = differenceInDays(deactivationDate, new Date());

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'compliantGracePeriodBanner',
				attributes: {
					daysLeft,
					licenseConsumingUserCount,
					edition,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, daysLeft, licenseConsumingUserCount, edition]);

	const onBillingClicked = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gracePeriodBannerBilling',
				source: 'compliantGracePeriodBanner',
				attributes: {
					daysLeft,
					licenseConsumingUserCount,
					edition,
				},
			},
		}).fire();
	};

	const onDismiss = () => {
		void setOnboardingState({
			key: TRIAL_COMPONENTS_EXPERIENCES.GRACE_PERIOD_BANNER_DISMISSED,
			value: 'true',
		});
		hideBanner();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'button',
				actionSubjectId: 'compliantGracePeriodBanner',
				source: 'compliantGracePeriodBanner',
				attributes: {
					daysLeft,
					licenseConsumingUserCount,
					edition,
				},
			},
		}).fire();
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		// can select an option with either Enter or spacebar
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			onDismiss();
		}
	};

	if (isNaN(daysLeft) || daysLeft < 0) {
		return null;
	}

	return (
		<CompliantWarningBanner
			bannerHeight={COMPLIANT_BANNER_HEIGHT}
			description={
				<>
					<FormattedMessage {...i18n.trialEndedWarning} />{' '}
					{daysLeft === 0 ? (
						<FormattedMessage
							{...i18n.lastDayWarning}
							values={{
								edition: capitalize(edition),
							}}
						/>
					) : (
						<FormattedMessage
							{...i18n.remainingDaysWarning}
							values={{
								numberOfDaysLeft: daysLeft,
								edition: capitalize(edition),
							}}
						/>
					)}
				</>
			}
			inlineDescriptionCTA={
				<BannerLink href={linkToAddBilling} target="_blank" onClick={onBillingClicked}>
					<FormattedMessage
						{...i18n.linkToPaymentDetails}
						values={{
							edition: capitalize(edition),
						}}
					/>
				</BannerLink>
			}
			rightSideComponent={
				<IconWrapper
					onClick={onDismiss}
					tabIndex={0}
					data-testid="banner-dismiss-icon"
					onKeyDown={handleKeyDown}
				>
					<CrossIcon label={formatMessage(i18n.dismissIcon)} size="small" />
				</IconWrapper>
			}
		/>
	);
};
